/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useLocation } from "@epic-mw/app-context";
import { emitEvent } from "@epic-mw/common-utils/dist/analyticsUtils";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";

import { useAccountId } from "../account";
import { useApiConfig } from "../api-config";
import { useAccountLoginRequired } from "../auth";

import {
	EulaLatestRes,
	getEulaData,
	EulaAcceptedRes,
	createEulaKey,
	getEulaAccepted,
	EulaAcceptRes,
	acceptEula,
} from "./eula.fetch";

export const useEulaContent = (
	key: string,
): {
	eulaContent?: EulaLatestRes;
	isLoading: boolean;
	error?: string;
} => {
	const { serverUrl } = useApiConfig();
	const shouldFetch = !!key;

	const { data, error } = useSWRImmutable(shouldFetch ? [key, serverUrl] : null, (k, url) => getEulaData(k, url), {
		errorRetryCount: 1,
	});

	return {
		eulaContent: data,
		isLoading: !data && !error,
		error,
	};
};

export const useEulaAccepted = (
	key: string,
	locale: string,
): {
	eulaAccepted?: EulaAcceptedRes;
	isLoading: boolean;
	error?: string;
} => {
	const { accountLoading } = useAccountLoginRequired();
	const accountId = useAccountId();

	const shouldFetch = !accountLoading && key && accountId;
	const request = createEulaKey({
		key,
		accountId,
		locale,
	});

	const { serverUrl } = useApiConfig();
	const { data, error } = useSWR(shouldFetch ? [request, serverUrl] : null, (req, url) => getEulaAccepted(req, url), {
		errorRetryCount: 1,
	});

	return {
		eulaAccepted: data,
		isLoading: accountLoading || (!data && !error),
		error,
	};
};

export const useEulaAcceptance = (
	key: string,
	locale: string,
	submit: boolean,
	version?: number,
): {
	accept?: EulaAcceptRes;
	isLoading: boolean;
	error?: string;
} => {
	const { accountLoading } = useAccountLoginRequired();
	const accountId = useAccountId();
	const location = useLocation();

	const shouldFetch = !accountLoading && key && accountId && version && submit;
	const request = createEulaKey({
		key,
		accountId,
		version,
		locale,
	});

	const { serverUrl } = useApiConfig();
	const { data, error } = useSWRImmutable(
		shouldFetch ? [request, serverUrl] : null,
		(req, url) => acceptEula(req, url),
		{ errorRetryCount: 1 },
	);

	if (shouldFetch && data && !error) {
		emitEvent(location, {
			eventAction: "client.eula.accept",
			eventLabel: key,
		});
	}

	return {
		accept: data,
		isLoading: accountLoading || (submit && !data && !error),
		error,
	};
};
